import { useEffect, useRef, useState } from 'react';
import { FormikHelpers } from 'formik';
import { AxiosRequestConfig } from 'axios';
import { callApi } from 'src/core/api/callApi';
import { clearCacheForUrl } from 'src/core/api/cache/idbUtility';
import { deleteTransferOrderRowsByTransferOrderId, getAllTransferOrders, getTransferOrderById, updateTransferOrderRowsByTransferOrderId } from 'src/core/api/apiRequests';
import { buildTransferOrderRequest } from 'src/core/forms/submit/submitRequests';
import { useMessageContext } from 'src/shared/support/MessageContext';

export const useTransferOrderSubmit = () => {
  const [result, setResult] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const active = useRef(false);
  const mounted = useRef(true);
  const cancel = useRef<() => void>();
  const created = useRef<any>(null);
  const completed = useRef(new Set<string>());
  const identity = useRef<any>();
  const { updateMessageStatus } = useMessageContext();
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; cancel.current?.(); }; }, []);

  const submit = async (values: any, rows: any[], helpers: FormikHelpers<any>) => {
    if (active.current) return;
    active.current = true;
    setSubmitting(true);
    setResult(null);
    if (identity.current !== values.OrderId) {
      identity.current = values.OrderId;
      created.current = null;
      completed.current.clear();
    }
    const send = (request: AxiosRequestConfig) => new Promise<any>((resolve, reject) => {
      cancel.current = callApi({ request, config: { isMountedRef: () => mounted.current, updateMessageStatus, logLevel: ['error'], resolve, reject } });
    });
    try {
      // Products are added after the new header has a persisted identity.
      if (!values.OrderId && !created.current) created.current = await send(buildTransferOrderRequest(values)) ?? {};
      if (!mounted.current) return;
      const orderId = values.OrderId || created.current?.OrderId;
      if (!orderId) {
        updateMessageStatus({ requestUrl: getAllTransferOrders(500, 0).url, status: 'error', message: 'The create response did not identify the Transfer Order. Check the created record before retrying.' });
        return;
      }
      if (values.OrderId) {
        const requests = [...transferOrderRowsPostRequests({ values, changedRows: rows }), buildTransferOrderRequest(values)];
        for (const request of requests) {
          const key = JSON.stringify([request.method, request.url, request.data]);
          if (completed.current.has(key)) continue;
          await send(request);
          if (!mounted.current) return;
          completed.current.add(key);
          await clearCacheForUrl(request.url);
        }
      }
      await clearCacheForUrl(getAllTransferOrders(500, 0).url);
      await clearCacheForUrl(getTransferOrderById(String(orderId)).url);
      const detail = await send(getTransferOrderById(String(orderId)));
      if (!mounted.current) return;
      if (String(detail?.OrderId) !== String(orderId) || !Array.isArray(detail?.TransferOrderRows)) {
        updateMessageStatus({ requestUrl: getTransferOrderById(String(orderId)).url, status: 'error', message: 'The order was saved, but its details could not be reloaded. Retry to reload before making more changes.' });
        return;
      }
      setResult(detail);
      completed.current.clear();
      updateMessageStatus({ requestUrl: getTransferOrderById(String(orderId)).url, status: 'success', message: 'Transfer Order saved.' });
    } catch {
      // Transport reports failures. Retain the draft and completed steps for a same-page retry.
    } finally {
      active.current = false;
      if (mounted.current) { setSubmitting(false); helpers.setSubmitting(false); }
    }
  };
  return [result, submit, submitting] as const;
};

export const transferOrderRowsPostRequests = ({ values, changedRows }: { values: any; changedRows: any[] }): AxiosRequestConfig[] => {
  const originalRows = values.TransferOrderRows ?? [];
  const currentRows = changedRows ?? [];
  const originalById = new Map<any, any>(originalRows.map(row => [String(row.OrderRowId), row]));
  const currentIds = new Set(currentRows.filter(row => row.OrderRowId > 0).map(row => String(row.OrderRowId)));
  const removed = originalRows.filter(row => row.OrderRowId > 0 && !currentIds.has(String(row.OrderRowId))).map(row => ({ OrderRowId: row.OrderRowId }));
  const added = currentRows.filter(row => !(row.OrderRowId > 0)).map(row => ({ ProductId: row.ProductId, Qty: Number(row.Qty), Comment: row.Comment ?? null }));
  const updated = currentRows.filter(row => {
    if (!(row.OrderRowId > 0)) return false;
    const original = originalById.get(String(row.OrderRowId));
    return !original || Number(original.Qty) !== Number(row.Qty) || (original.Comment ?? '') !== (row.Comment ?? '');
  }).map(row => ({ OrderRowId: row.OrderRowId, ProductId: row.ProductId, Qty: Number(row.Qty), Comment: row.Comment ?? null }));
  const requests: AxiosRequestConfig[] = [];
  if (updated.length) requests.push(updateTransferOrderRowsByTransferOrderId(values.OrderId, updated));
  if (added.length) requests.push(updateTransferOrderRowsByTransferOrderId(values.OrderId, added, 'POST'));
  if (removed.length) requests.push(deleteTransferOrderRowsByTransferOrderId(values.OrderId, removed));
  return requests;
};
